import React, { useState, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ChurchIcon from "@mui/icons-material/Church";
import ExploreIcon from "@mui/icons-material/Explore";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MenuIcon from "@mui/icons-material/Menu";
import MobileMoreMenu from "./MobileMoreMenu";

const MobileLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const navigate = useNavigate();
  const location = useLocation();

  // Create theme based on system preference
  const mobileTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDarkMode ? "dark" : "light",
          background: {
            default: prefersDarkMode ? "#000000" : "#f5f5f7",
            paper: prefersDarkMode ? "#1c1c1e" : "#ffffff",
          },
          primary: {
            main: "#0a84ff",
          },
          text: {
            primary: prefersDarkMode ? "#ffffff" : "#000000",
            secondary: prefersDarkMode
              ? "rgba(235, 235, 245, 0.6)"
              : "rgba(60, 60, 67, 0.6)",
          },
        },
        typography: {
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", "PingFang SC", "Microsoft YaHei", sans-serif',
          h4: { fontWeight: 700, fontSize: "1.75rem", letterSpacing: "-0.02em" },
          h5: { fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.015em" },
          h6: { fontWeight: 600, fontSize: "1.05rem", letterSpacing: "-0.01em" },
          body1: { fontSize: "0.95rem", lineHeight: 1.5 },
          body2: { fontSize: "0.85rem", lineHeight: 1.45 },
          button: { textTransform: "none", fontWeight: 500 },
        },
        components: {
          MuiBottomNavigation: {
            styleOverrides: {
              root: {
                // 苹果液态玻璃效果
                background: prefersDarkMode
                  ? "rgba(28, 28, 30, 0.7)"
                  : "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                borderTop: prefersDarkMode
                  ? "0.5px solid rgba(255, 255, 255, 0.1)"
                  : "0.5px solid rgba(0, 0, 0, 0.08)",
                boxShadow: prefersDarkMode
                  ? "0 -1px 20px rgba(0, 0, 0, 0.3)"
                  : "0 -1px 20px rgba(0, 0, 0, 0.05)",
                height: "64px",
                paddingBottom: "env(safe-area-inset-bottom)",
              },
            },
          },
          MuiBottomNavigationAction: {
            styleOverrides: {
              root: {
                color: prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)",
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "60px",
                "&.Mui-selected": {
                  color: "#0a84ff",
                  "& .MuiBottomNavigationAction-label": {
                    fontSize: "0.7rem",
                    fontWeight: 500,
                  },
                },
                "& .MuiBottomNavigationAction-label": {
                  fontSize: "0.7rem",
                  marginTop: "4px",
                  fontWeight: 400,
                },
              },
            },
          },
        },
      }),
    [prefersDarkMode]
  );

  // Determine active bottom tab
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === "/") return 0;
    if (path.startsWith("/worship")) return 1;
    if (path.startsWith("/vision")) return 2;
    return 3; // "More" or other pages
  };

  const [value, setValue] = React.useState(getCurrentTab());

  React.useEffect(() => {
    setValue(getCurrentTab());
  }, [location.pathname]);

  const handleNavigation = (newValue) => {
    if (newValue === 3) {
      // Open the side drawer when "More" is clicked
      setMenuOpen(true);
    } else {
      setValue(newValue);
      switch (newValue) {
        case 0:
          navigate("/");
          break;
        case 1:
          navigate("/worship");
          break;
        case 2:
          navigate("/vision");
          break;
        default:
          break;
      }
    }
  };

  return (
    <ThemeProvider theme={mobileTheme}>
      <Box
        sx={{
          bgcolor: "background.default",
          minHeight: "100dvh", // 使用动态视口高度，支持安全区域
          color: "text.primary",
          display: "flex",
          flexDirection: "column",
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "calc(64px + env(safe-area-inset-bottom))", // 底部导航栏高度 + 安全区域
          position: "relative",
        }}
      >
        {/* Content Area */}
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>

        {/* Bottom Navigation - 苹果液态玻璃效果 */}
        <BottomNavigation
          showLabels
          value={value}
          onChange={(event, newValue) => {
            handleNavigation(newValue);
          }}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <BottomNavigationAction label="主页" icon={<HomeIcon />} />
          <BottomNavigationAction label="崇拜" icon={<ChurchIcon />} />
          <BottomNavigationAction label="异象" icon={<ExploreIcon />} />
          <BottomNavigationAction label="更多" icon={<MoreHorizIcon />} />
        </BottomNavigation>

        {/* Navigation Drawer */}
        <MobileMoreMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          prefersDarkMode={prefersDarkMode}
        />
      </Box>
    </ThemeProvider>
  );
};

export default MobileLayout;
